import { useMutation } from "@tanstack/react-query";
import { Download, TriangleAlert } from "lucide-react";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../api/client.js";
import { useSession } from "../auth/session.js";
import { Button, Card, ErrorNotice, Field, Input } from "./form.js";

const downloadClass =
  "inline-flex h-10 items-center gap-2 rounded-full border border-ink-3/40 px-5 text-sm font-medium text-accent hover:bg-accent-soft/60";

/** Settings → Security: a member takes their own data away. */
export function AccountExportCard() {
  const { t } = useTranslation();
  return (
    <Card title={t("dataControls.account.title")}>
      <p className="text-sm text-ink-2">{t("dataControls.account.body")}</p>
      <div>
        <a href={api.workspace.accountExportUrl()} download className={downloadClass}>
          <Download className="size-4" />
          {t("dataControls.account.download")}
        </a>
      </div>
    </Card>
  );
}

/** Admin console → Billing: the workspace's records, and closing it (owner only). */
export function WorkspaceDataCards() {
  const { t, i18n } = useTranslation();
  const session = useSession();
  const isOwner = session.data?.user.role === "owner";
  const [confirmName, setConfirmName] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const close = useMutation({
    mutationFn: () => api.workspace.close({ confirmName, currentPassword }),
    // Every session is revoked; leave for the public site once the owner has read the date.
    onSuccess: () => setTimeout(() => window.location.assign("/"), 6000),
  });
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (window.confirm(t("dataControls.close.confirm"))) close.mutate();
  };
  const date = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: "long" }).format(new Date(iso));

  return (
    <>
      <Card title={t("dataControls.workspace.title")}>
        <p className="text-sm text-ink-2">{t("dataControls.workspace.body")}</p>
        <div>
          <a href={api.workspace.exportUrl()} download className={downloadClass}>
            <Download className="size-4" />
            {t("dataControls.workspace.download")}
          </a>
        </div>
      </Card>
      {isOwner ? (
        <section className="rounded-2xl border border-danger/40 bg-surface p-5 md:p-6">
          <h2 className="mb-2 flex items-center gap-2 text-base font-medium text-danger">
            <TriangleAlert className="size-5" />
            {t("dataControls.close.title")}
          </h2>
          {close.data ? (
            <p className="text-sm text-ink-2">
              {t("dataControls.close.done", { date: date(close.data.deleteAfter) })}
            </p>
          ) : (
            <form onSubmit={onSubmit} className="flex flex-col gap-4">
              <p className="text-sm text-ink-2">
                {t("dataControls.close.body", { name: session.data?.tenant.name ?? "" })}
              </p>
              <Field
                label={t("dataControls.close.name", { name: session.data?.tenant.name ?? "" })}
              >
                <Input
                  value={confirmName}
                  onChange={(e) => setConfirmName(e.target.value)}
                  autoComplete="off"
                  required
                />
              </Field>
              <Field label={t("dataControls.close.password")}>
                <Input
                  type="password"
                  autoComplete="current-password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                />
              </Field>
              <ErrorNotice error={close.error} />
              <div>
                <Button
                  type="submit"
                  variant="danger"
                  disabled={
                    close.isPending ||
                    !currentPassword ||
                    confirmName.trim() !== (session.data?.tenant.name ?? "").trim()
                  }
                >
                  {t("dataControls.close.submit")}
                </Button>
              </div>
            </form>
          )}
        </section>
      ) : null}
    </>
  );
}
